import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, Zap, Package, Keyboard, MousePointer, Map, Users, Clock, Sparkles, ChevronRight, X } from 'lucide-react';

const Home: React.FC = () => {
  const [dialogStep, setDialogStep] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showNpc, setShowNpc] = useState(true);

  // Set to true to re-enable the Maple Admin NPC dialog
  const SHOW_NPC_DIALOG = false;

  const expRate = process.env.REACT_APP_EXP_RATE || '6';
  const mesoRate = process.env.REACT_APP_MESO_RATE || '1';
  const dropRate = process.env.REACT_APP_DROP_RATE || '2';

  const dialogMessages = [
    `Ah, a chosen one... Welcome to #${process.env.REACT_APP_SERVER_NAME}#. You've earned your place here.`,
    `We run *x${expRate} EXP*, *x${mesoRate} Meso*, and *x${dropRate} Drop* — balanced for a community that actually plays together.`,
    "This is *whitelist-only* Maple World. Curated. Intentional. Built for those who appreciate the craft."
  ];

  useEffect(() => {
    if (!showNpc) return;
    const currentMessage = dialogMessages[dialogStep];
    if (displayedText.length < currentMessage.length) {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setDisplayedText(currentMessage.slice(0, displayedText.length + 1));
      }, 20);
      return () => clearTimeout(timer);
    } else {
      setIsTyping(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedText, dialogStep, showNpc]);

  useEffect(() => {
    setDisplayedText('');
    setIsTyping(true);
  }, [dialogStep]);

  const nextDialog = () => {
    if (isTyping) {
      setDisplayedText(dialogMessages[dialogStep]);
      setIsTyping(false);
    } else if (dialogStep < dialogMessages.length - 1) {
      setDialogStep(dialogStep + 1);
    } else {
      setShowNpc(false);
    }
  };

  const closeNpc = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNpc(false);
  };

  const formatText = (text: string) => {
    return text.split(/(\*[^*]+\*|#[^#]+#)/g).map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <span key={index} className="text-daruma-red font-semibold">{part.slice(1, -1)}</span>;
      }
      if (part.startsWith('#') && part.endsWith('#')) {
        return <span key={index} className="font-bold text-white">{part.slice(1, -1)}</span>;
      }
      return part;
    });
  };

  const qolFeatures = [
    { icon: <Zap className="w-5 h-5" />, title: 'Auto Job Advance', desc: 'Automatic job advancement' },
    { icon: <Package className="w-5 h-5" />, title: 'Stackable Items', desc: 'Stack up to 30,000 items' },
    { icon: <Keyboard className="w-5 h-5" />, title: 'Quick Slots', desc: 'Extended hotkey system' },
    { icon: <MousePointer className="w-5 h-5" />, title: 'Auto Loot', desc: 'Pet auto-loot included' },
    { icon: <Map className="w-5 h-5" />, title: 'World Map', desc: 'Enhanced navigation' },
    { icon: <Users className="w-5 h-5" />, title: 'Party Search', desc: 'Easy party finding' },
    { icon: <Clock className="w-5 h-5" />, title: '24/7 Uptime', desc: 'Stable server always online' },
    { icon: <Sparkles className="w-5 h-5" />, title: 'Custom Events', desc: 'Weekly events & rewards' },
  ];

  const NpcDialog = () => (
    <div onClick={nextDialog} className="cursor-pointer group select-none">
      <div className="flex gap-4 items-end">
        {/* NPC image */}
        <div className="flex-shrink-0">
          <img
            src="/assets/npc/1.png"
            alt="Maple Admin"
            className="w-[130px] h-auto drop-shadow-2xl group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* Dialog bubble — flat, ink-border style */}
        <div className="relative max-w-[400px]">
          {/* tail */}
          <div className="absolute -left-2 bottom-5 w-4 h-4 bg-white dark:bg-daruma-deeper rotate-45 border-l-2 border-b-2 border-daruma-ink dark:border-gray-500" />

          <div className="relative bg-white dark:bg-daruma-deeper border-2 border-daruma-ink dark:border-gray-500 p-5 shadow-2xl">
            {/* red top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-daruma-red" />

            {/* close */}
            <button
              onClick={closeNpc}
              className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-daruma-red transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            {/* name tag */}
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 bg-daruma-red mb-3">
              <span className="text-xs font-bold text-white uppercase tracking-widest">Maple Admin</span>
            </div>

            {/* text */}
            <div className="h-[80px] overflow-hidden">
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                {formatText(displayedText)}
                {isTyping && <span className="inline-block w-0.5 h-3.5 ml-0.5 bg-daruma-red animate-pulse" />}
              </p>
            </div>

            {/* nav */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex gap-1.5">
                {dialogMessages.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 transition-all ${
                      i === dialogStep
                        ? 'bg-daruma-red w-5'
                        : i < dialogStep
                        ? 'bg-daruma-red/40'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
              <span className="flex items-center gap-1 text-xs text-daruma-red font-bold uppercase tracking-wide">
                {isTyping ? 'Skip' : dialogStep < dialogMessages.length - 1 ? 'Next' : 'Close'}
                <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative min-h-[580px] lg:min-h-[680px] flex items-center overflow-hidden bg-daruma-ink">

        {/* Background gif */}
        <img
          src="/assets/hero.gif"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: 0.35 }}
        />

        {/* Dark overlay — left-heavy so text stays readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-daruma-ink/90 via-daruma-ink/70 to-daruma-ink/40 pointer-events-none" />

        {/* Vertical red stripe — left edge */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-daruma-red" />

        {/* Horizontal red rule at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-daruma-red/40" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 w-full">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">

            {/* ── Left: copy ── */}
            <div className="flex-1 animate-fade-in">
              {/* eyebrow */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-daruma-red" />
                <span className="text-daruma-red text-xs font-bold uppercase tracking-[0.25em]">
                  DARUMA.GG
                </span>
              </div>

              {/* Server name */}
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black text-white leading-none tracking-tight mb-2">
                {process.env.REACT_APP_SERVER_NAME || 'Maple'}
              </h1>
              {/* red underline accent */}
              <div className="w-24 h-1.5 bg-daruma-red mb-8" />

              <p className="text-gray-400 text-base leading-relaxed mb-10 max-w-md">
                A whitelist-only World rooted in Japanese spirit. Not every adventurer finds their way here — but those who do, belong.
              </p>

              {/* Rates — vertical left-border style */}
              <div className="flex gap-8 mb-10">
                {[
                  { val: expRate, label: 'EXP' },
                  { val: mesoRate, label: 'Meso' },
                  { val: dropRate, label: 'Drop' },
                ].map(({ val, label }) => (
                  <div key={label} className="border-l-2 border-daruma-red pl-4">
                    <div className="text-3xl font-black text-daruma-red leading-none">×{val}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* CTAs — sharp, no border-radius */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/download"
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-daruma-red hover:bg-daruma-crimson text-white font-bold text-sm uppercase tracking-wider transition-all duration-200 hover:translate-x-0.5 hover:translate-y-[-1px] shadow-lg shadow-daruma-red/30"
                >
                  <Download className="w-4 h-4" />
                  Download Client
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 border-2 border-white/30 text-white font-bold text-sm uppercase tracking-wider hover:border-daruma-red hover:text-daruma-red transition-all duration-200"
                >
                  Create Account
                </Link>
              </div>
            </div>

            {/* ── Right: NPC dialog ── */}
            {SHOW_NPC_DIALOG && showNpc && (
              <div className="hidden lg:flex lg:flex-shrink-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <NpcDialog />
              </div>
            )}
          </div>

          {/* Mobile NPC */}
          {SHOW_NPC_DIALOG && showNpc && (
            <div className="lg:hidden mt-10 animate-fade-in">
              <NpcDialog />
            </div>
          )}
        </div>

        {/* Bottom fade into features section */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-daruma-ink to-transparent" />
      </section>

      {/* ── FEATURES ── */}
      <section className="relative bg-daruma-ink overflow-hidden pb-24 pt-16">
        {/* Seigaiha pattern */}
        <div
          className="absolute inset-0 seigaiha pointer-events-none"
          style={{ color: 'rgba(255,255,255,0.025)' }}
        />

        {/* Red top border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-daruma-red/30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          {/* Section header */}
          <div className="flex items-center gap-4 mb-12">
            <div className="w-8 h-px bg-daruma-red" />
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-daruma-red">Features</h2>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <h3 className="text-3xl font-black text-white mb-12 -mt-8 pl-12">
            Quality of Life
          </h3>

          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 gap-px bg-white/10 flex-1">
              {qolFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-daruma-ink p-6 group hover:bg-daruma-charcoal transition-colors duration-200 relative"
                >
                  {/* red left accent on hover */}
                  <div className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-0.5 bg-daruma-red transition-all duration-200" />

                  <div className="w-10 h-10 border border-daruma-red/50 group-hover:border-daruma-red flex items-center justify-center text-daruma-red mb-4 transition-colors">
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-white text-sm mb-1">{feature.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Discord widget */}
            <div className="hidden lg:flex flex-shrink-0 justify-center">
              <iframe
                src="https://discord.com/widget?id=907607762861686785&theme=dark"
                width="350"
                height="500"
                allowTransparency={true}
                frameBorder={0}
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                title="Discord Widget"
                className="border border-white/10"
              />
            </div>
          </div>

          {/* Mobile Discord widget */}
          <div className="lg:hidden flex justify-center mt-10">
            <iframe
              src="https://discord.com/widget?id=907607762861686785&theme=dark"
              width="350"
              height="500"
              allowTransparency={true}
              frameBorder={0}
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
              title="Discord Widget"
              className="border border-white/10"
            />
          </div>
        </div>

        {/* Bottom fade to page background */}
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-daruma-charcoal/50 to-transparent" />
      </section>
    </div>
  );
};

export default Home;
